import { AddressLine } from '@components/Address/AddressLine';
import { ProposalShoppingCart } from '@components/BookingProposal/ProposalShoppingCart';
import { RejectButtonAndModal } from '@components/BookingProposal/RejectButtonAndModal';
import { BrandImage } from '@components/Brand/BrandImage';
import { Trans } from '@lingui/react/macro';
import { BookingProposalRecord } from '@navio/nsdk';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiLink } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { commonTrackingEvents, tracking } from 'noddi-web';
import { useProposalCatalogPrices } from '../../hooks/useProposalCatalogPrices';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import {
  useBookingProposalActions,
  useBookingProposalAddress,
  useBookingProposalAnalytics,
  useBookingProposalItems
} from '../../stores/bookingProposalStore';
import { isRecallProposalType } from '../../utils/bookingProposalType';
import { usePopulateBookingStoreFromProposal } from './hooks/usePopulateBookingStoreFromProposal';

type Props = {
  proposal: BookingProposalRecord;
};

export const ProposalCard = ({ proposal }: Props) => {
  // we can use static data from api
  const { slug, type, id } = proposal;

  // need use data from store as it may be updated
  const address = useBookingProposalAddress();
  const bookingProposalItems = useBookingProposalItems();
  const { clearStore } = useBookingProposalActions();
  const analytics = useBookingProposalAnalytics();
  const { prices: catalogPrices } = useProposalCatalogPrices(proposal);

  const populateBookingStoreFromProposal = usePopulateBookingStoreFromProposal();

  const { currentBrand } = useCurrentBrand();

  // this only works for recall proposals, as they have services we find in booking flow, we hide it for mounting jobs etc
  function handleChangeServices() {
    populateBookingStoreFromProposal({
      bookingProposalItems,
      brand: proposal.serviceDepartment.brand,
      // address is always defined when isRecallProposal
      address: address!,
      proposalId: proposal.id,
      customer: {
        userGroupId: proposal.userGroupMember.userGroup.id,
        userId: proposal.userGroupMember.user.id
      }
    });
  }

  const isRecallProposal = isRecallProposalType(type.value);

  return (
    <NoddiBasicCard className='flex flex-col overflow-hidden p-0'>
      <BrandImage brand={currentBrand} />

      <div className='flex flex-col gap-4 p-4 md:p-5'>
        {address && (
          <div className='flex w-full justify-between gap-4'>
            <AddressLine address={address} />
            <NoddiLink to={customerRoutes.editProposalAddress.getPath({ slug })} variant='outline' size='extraSmall'>
              <Trans>Edit</Trans>
            </NoddiLink>
          </div>
        )}
        {isRecallProposal && (
          <div className='flex w-full items-center justify-between gap-4'>
            <div className='flex items-baseline gap-2'>
              <NoddiIcon name='WheelAngle' />
              <Trans>Cars and services</Trans>
            </div>

            <NoddiLink
              to={customerRoutes.newBookingCars.getPath()}
              onClick={handleChangeServices}
              variant='outline'
              size='extraSmall'
            >
              <Trans>Change</Trans>
            </NoddiLink>
          </div>
        )}
        <ProposalShoppingCart
          userGroupId={proposal.userGroupMember.userGroup.id}
          serviceDepartmentId={proposal.serviceDepartment.id}
          bookingProposalItems={bookingProposalItems}
          catalogPrices={catalogPrices}
        />

        <div className='flex flex-col'>
          <NoddiLink
            variant='success'
            className='w-full'
            endIcon='ArrowRight'
            to={
              address
                ? customerRoutes.bookingProposalTimeWindowPicker.getPath({ slug })
                : customerRoutes.bookingProposalAddress.getPath({ slug })
            }
          >
            {address ? <Trans>Choose time</Trans> : <Trans>Choose time and address</Trans>}
          </NoddiLink>
          {isRecallProposal && (
            <RejectButtonAndModal
              id={id}
              onRejected={() => {
                tracking.track(commonTrackingEvents.proposalRejected, analytics);
                clearStore();
              }}
            />
          )}
        </div>
      </div>
    </NoddiBasicCard>
  );
};
